import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import Popup from 'components/popup/Popup'
import { DragAndDropUploadPhotos } from 'pages/BuffetLinePage/components/DragAndDropUploadPhotos'

import AppBtn, {
  AppBtnColor,
  AppBtnSize,
  IconPosition,
} from 'components/btn/AppBtn'
import { AppFilterMultiDropdown } from 'components/AppDropdown/AppFilterMultiDropdown'
import { useGetMismatchTypeListQuery } from 'core/api/TypeMismatchApi'
import {
  AppNotification,
  NotificationType,
} from 'components/notification/Notification'
import { useUploadStandardAiDatasetsMutation } from 'core/api/StandardAiDataset'
import { useSearchParams, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

type StatusType = 'match' | 'no-match' | 'processing' | 'not-labeled'

type Props = {
  setIsDirty?: (value: boolean) => void
  onClose?: () => void
}

const FileUploadDishAIPopup: React.FC<Props> = ({
  setIsDirty,
  onClose,
}) => {
  const [fileURLs, setFilePreview] = useState<string[]>([])
  const [status, setStatus] = React.useState<StatusType>('not-labeled')
  const [mismatchTypeIds, setMismatchTypeIds] = React.useState<number[] | null>(
    null,
  )

  const { data: mismatchTypeList } = useGetMismatchTypeListQuery({
    is_archived: false,
  })

  const mismatchOptions = useMemo(
    () =>
      mismatchTypeList?.results
        ? mismatchTypeList.results.map((r) => ({ id: r.id, name: r.name }))
        : [],
    [mismatchTypeList],
  )

  const [searchParams] = useSearchParams()
  const { t } = useTranslation()

  const params = useParams<{ dishId?: string }>()
  const routeDishId = params.dishId ? Number(params.dishId) : undefined

  const [localFiles, setLocalFiles] = useState<File[]>([])

  const previewUrlsRef = useRef<string[]>([])

  useEffect(() => {
    previewUrlsRef.current = fileURLs ?? []
    setLocalFiles((prev) => {
      const newLength = (fileURLs ?? []).length
      if (newLength < prev.length) {
        return prev.slice(0, newLength)
      }
      return prev
    })
  }, [fileURLs])

  const [uploadStandardDatasets, { isLoading: isUploading }] =
    useUploadStandardAiDatasetsMutation()

  const revokePreviews = useCallback(() => {
    if (previewUrlsRef.current.length > 0) {
      previewUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url)
        } catch {
          // ignore
        }
      })
      previewUrlsRef.current = []
    }
  }, [])

  const handleReplaceFileAtIndex = useCallback(
    (file: File, index: number) => {
      setLocalFiles((prev) => {
        const next = [...prev]
        next[index] = file
        return next
      })
      const newPreviewUrl = URL.createObjectURL(file)
      const newPreviews = [...previewUrlsRef.current]
      newPreviews[index] = newPreviewUrl
      previewUrlsRef.current = newPreviews
      setFilePreview(newPreviews)
      setIsDirty?.(true)
    },
    [setFilePreview, setIsDirty],
  )

  const handleSetFiles = useCallback(
    (images: File[]) => {
      setLocalFiles((prev) => [...prev, ...images])
      setIsDirty?.(true)
      if (images.length > 0) {
        try {
          const previews = images.map((f) => URL.createObjectURL(f))
          const allPreviews = [...previewUrlsRef.current, ...previews]
          previewUrlsRef.current = allPreviews
          setFilePreview(allPreviews)
        } catch {
          // ignore
        }
      } else {
        previewUrlsRef.current = []
        setFilePreview([])
      }
    },
    [setIsDirty, setFilePreview, revokePreviews],
  )

  useEffect(() => {
    return () => {
      revokePreviews()
    }
  }, [revokePreviews])

  const handleCancel = () => {
    revokePreviews()
    setLocalFiles([])
    setFilePreview([])
    setIsDirty?.(false)

    onClose && onClose()
  }

  const statusMap: Record<string, string> = {
    'not-labeled': 'Not_determined',
    match: 'Compliant',
    'no-match': 'Non_compliant',
    processing: 'Processing',
  }

  const handleContinue = async () => {
    try {
      if (!routeDishId) {
        AppNotification({
          msg: t('Не задан dish_id'),
          type: NotificationType.error,
        })
        return
      }

      // Отправка через multipart/form-data
      if (localFiles.length > 0) {
        const totalSize = localFiles.reduce((sum, f) => sum + f.size, 0)
        if (totalSize > 30 * 1024 * 1024) {
          AppNotification({
            msg: t('Суммарный размер файлов превышает 30 МБ'),
            type: NotificationType.error,
          })
          return
        }

        const typeIds =
          mismatchTypeIds && mismatchTypeIds.length > 0 ? mismatchTypeIds : ['']

        const errors: string[] = []

        try {
          const form = new FormData()
          localFiles.forEach((f) => {
            const filename = f.name || `image_${Date.now()}.jpg`
            form.append('images', f, filename)
          })
          form.append('dish_id', String(routeDishId))
          form.append(
            'compliance_with_standards',
            statusMap[status] || 'Not_determined',
          )
          if (
            status === 'no-match' &&
            mismatchTypeIds &&
            mismatchTypeIds.length > 0
          ) {
            mismatchTypeIds.forEach((id) => {
              form.append('non_compliant_type_ids', String(id))
            })
          }

          await uploadStandardDatasets(form).unwrap()
        } catch (err) {}

        if (errors.length > 0) {
          AppNotification({
            msg: t(`Ошибка при загрузке для типов: ${errors.join(', ')}`),
            type: NotificationType.error,
          })
          return
        }

        AppNotification({
          msg: t('Файлы успешно загружены'),
          type: NotificationType.success,
        })

        revokePreviews()
        setLocalFiles([])
        setFilePreview([])
    
        setIsDirty?.(false)
        onClose && onClose()
        return
      }

      AppNotification({
        msg: t('Файлы не выбраны'),
        type: NotificationType.error,
      })
    } catch (err) {
      console.error('Upload failed', err)
      AppNotification({
        msg: t('Ошибка при загрузке файлов'),
        type: NotificationType.error,
      })
    }
  }

  return (
    <Popup closePopup={handleCancel}>
      <>
        <div className="upload-popup__container">
          <div className="upload-popup__header">
            <span>Загрузка файлов</span>
          </div>

          <div className="upload-popup__file upload-message--inline">
            <DragAndDropUploadPhotos
              fileURLs={fileURLs}
              onSetFiles={handleSetFiles}
              setFilePreview={setFilePreview}
              onReplaceFileAtIndex={handleReplaceFileAtIndex}
              setIsDirty={setIsDirty}
              extraText="для загрузки"
              shortMessage={true}
            />
          </div>

          <div className="upload-popup__body">
            <div className="upload-popup__status">
              <span className="upload-popup__label">Статус</span>
              <div className="upload-popup__radios">
                <label className="upload-popup__radio-item">
                  <input
                    type="radio"
                    name="reportStatus"
                    checked={status === 'not-labeled'}
                    onChange={() => setStatus('not-labeled')}
                  />
                  <span>Не размечено</span>
                </label>
                <label className="upload-popup__radio-item">
                  <input
                    type="radio"
                    name="reportStatus"
                    checked={status === 'match'}
                    onChange={() => setStatus('match')}
                  />
                  <span>Соответствует</span>
                </label>
                <label className="upload-popup__radio-item">
                  <input
                    type="radio"
                    name="reportStatus"
                    checked={status === 'no-match'}
                    onChange={() => setStatus('no-match')}
                  />
                  <span>Не соответствует</span>
                </label>
              </div>
            </div>

            {status === 'no-match' && (
              <div className="upload-popup__type">
                <label className="upload-popup__label">
                  Тип несоответствия
                </label>
                <AppFilterMultiDropdown<{ id: number; name: string }, any>
                  data={mismatchOptions}
                  searchParam="mismatchType"
                  propToShowInList="name"
                  valuePropName="id"
                  placeholder="Выбрать"
                  setCurrentValue={(values) => {
                    setMismatchTypeIds(values.map((v) => v.id))
                  }}
                  border
                  fullWidth
                />
              </div>
            )}
          </div>

          <div className="upload-popup__footer buttons-block ">
            <AppBtn
              color={AppBtnColor.whiteOutlineViolet}
              title={'Отменить'}
              sized={AppBtnSize.md}
              iconClassName={'close'}
              onClick={handleCancel}
            />
            <AppBtn
              color={AppBtnColor.violetWhite}
              title={'Продолжить'}
              sized={AppBtnSize.md}
              iconPosition={IconPosition.right}
              iconClassName={'arrow'}
              onClick={handleContinue}
              isLoading={isUploading}
            />
          </div>
        </div>
      </>
    </Popup>
  )
}

export default FileUploadDishAIPopup
